import React, { useState, useEffect, useMemo } from "react";
import { Staff, StaffDialogProps, StaffFormData } from "../types/staff";
import GenericForm, { FormFieldConfig } from "../../../../components/genericform/GenericForm";
import ImageUpload from "../../products/components/ImageUpload";
import { ToastContainer } from "react-toastify";
import { notifySuccess } from "src/assets/toastService";

interface EditStaffDialogProps extends StaffDialogProps {
  staff: Staff | null;
  onUpdateStaff: (id: string, formData: StaffFormData) => Promise<boolean>;
}

const EditStaffDialog: React.FC<EditStaffDialogProps> = ({ open, onClose, staff, onUpdateStaff }) => {
  const [formData, setFormData] = useState<StaffFormData>({
    staff_name: "",
    phone_number: "",
    image: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (staff) {
      setFormData({
        staff_name: staff.staff_name,
        phone_number: staff.phone_number,
        image: null,
      });
    }
  }, [staff]);

  const handleImageChange = (file: File | null) => {
    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handleSubmit = async () => {
    if (isSubmitting || !staff) return;

    setIsSubmitting(true);
    try {
      const success = await onUpdateStaff(staff._id.$oid, formData);

      if (success) {
        // Small delay to ensure toast is shown before closing
        setTimeout(() => {
          notifySuccess(`Staff member "${formData.staff_name}" updated successfully.`);
          handleClose();
          setIsSubmitting(false);
        }, 500);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ staff_name: "", phone_number: "", image: null });
      onClose();
    }
  };

  const isFormValid = useMemo(() => {
    if (!staff) return false;

    const nameValid = formData.staff_name.trim() !== "";
    const phoneValid = /^\d{8}$/.test(formData.phone_number);

    const changed = formData.staff_name !== staff.staff_name || formData.phone_number !== staff.phone_number || formData.image !== null;

    return nameValid && phoneValid && changed;
  }, [formData, staff]);

  const fields: FormFieldConfig[] = [
    {
      id: "staff_name",
      label: "Name",
      required: true,
      value: formData.staff_name,
      onChange: (val: string) => setFormData((prev) => ({ ...prev, staff_name: val })),
    },
    {
      id: "phone_number",
      label: "Phone Number",
      type: "phone",
      required: true,
      value: formData.phone_number,
      onChange: (val: string) => {
        if (/^\d{0,8}$/.test(val)) {
          setFormData((prev) => ({ ...prev, phone_number: val }));
        }
      },
    },
  ];

  return (
    <GenericForm open={open} title="Edit Staff Details" fields={fields} onClose={handleClose} onSubmit={handleSubmit} isFormValid={() => isFormValid} confirmLabel="Confirm" cancelLabel="Cancel">
      <div className="staff-image-input mt-3">
        <ImageUpload value={formData.image} onChange={handleImageChange} />

        {!formData.image && staff?.staff_image_url && (
          <div className="mt-2">
            <p className="text-sm text-gray-600 mb-1">Current Image:</p>
            <img src={staff.staff_image_url} alt="Current staff" className="w-[50px] h-[50px] object-cover rounded border border-gray-300" />
          </div>
        )}
      </div>
      <ToastContainer />
    </GenericForm>
  );
};

export default EditStaffDialog;
